/**
 * Enhanced URL validation utility for security considerations
 * Validates and normalizes URLs to ensure they have proper protocols
 */

/**
 * Validates and normalizes a URL string by ensuring it has a proper protocol.
 * If the URL doesn't start with http:// or https://, it automatically prepends https://
 *
 * @param url - The URL string to validate and normalize
 * @returns The validated URL with proper protocol, or the original URL if it's already valid
 * @throws Will not throw but returns a normalized URL instead
 *
 * @example
 * ```typescript
 * validateUrl("example.com") // Returns "https://example.com"
 * validateUrl("https://example.com") // Returns "https://example.com"
 * validateUrl("http://example.com") // Returns "http://example.com"
 * validateUrl("invalid-url") // Returns "https://invalid-url"
 * ```
 */
export const validateUrl = (url: string): string => {
  try {
    // Try to create a URL object to validate the format
    new URL(url);
    return url;
  } catch {
    // If URL creation fails, assume it needs https:// protocol
    return `https://${url}`;
  }
};

/**
 * Checks if a URL string has a valid protocol (http:// or https://)
 *
 * @param url - The URL string to check
 * @returns True if the URL has a valid protocol, false otherwise
 *
 * @example
 * ```typescript
 * hasValidProtocol("https://example.com") // Returns true
 * hasValidProtocol("example.com") // Returns false
 * ```
 */
export const hasValidProtocol = (url: string): boolean => {
  return url.startsWith("http://") || url.startsWith("https://");
};

/**
 * Safely opens a URL in a new tab with proper validation
 *
 * @param url - The URL to open
 * @param target - The target window/tab (defaults to "_blank")
 * @returns True if the URL was opened successfully, false otherwise
 *
 * @example
 * ```typescript
 * safeOpenUrl("example.com") // Opens https://example.com in new tab
 * safeOpenUrl("https://example.com", "_self") // Opens in current tab
 * ```
 */
export const safeOpenUrl = (url: string, target: string = "_blank"): boolean => {
  try {
    const validatedUrl = validateUrl(url);
    window.open(validatedUrl, target);
    return true;
  } catch (error) {
    console.error("Failed to open URL:", error);
    return false;
  }
};
